import type { ReactNode } from 'react';
import type { BillingPlan } from '../types';
import {
  buildPlanChangeUrl,
  buildPlanCtaUrl,
  shouldShowPlanDirectPurchaseLink,
  shouldUseCurrentPlanCta,
  shouldUsePlanChangeCta,
  shouldUseGetStartedLabel,
  usesDirectPurchaseOnlyVariant,
  type PlanCtaContext,
} from './plan-cta';

export interface PlanHeaderCopy {
  getStartedLabel: string;
  freeTrialLabel: string;
  currentPlanLabel: string;
  upgradeLabel: string;
  downgradeLabel: string;
}

export interface PlanHeaderPropsBundle {
  rateText: ReactNode;
  buttonHref?: string;
  buttonLabel: string;
  buttonAction:
    | 'current-plan'
    | 'downgrade'
    | 'get-started'
    | 'start-free-trial'
    | 'upgrade';
  buttonDisabled: boolean;
  directPurchaseHref: string;
  showDirectPurchaseLink: boolean;
}

export function getPlanHeaderProps(
  plan: BillingPlan,
  context: PlanCtaContext,
  labels: PlanHeaderCopy,
  rateText: ReactNode,
): PlanHeaderPropsBundle {
  if (shouldUseCurrentPlanCta(plan, context)) {
    return {
      rateText,
      buttonHref: undefined,
      buttonLabel: labels.currentPlanLabel,
      buttonAction: 'current-plan',
      buttonDisabled: true,
      directPurchaseHref: buildPlanCtaUrl(plan, context, {
        directPurchase: true,
      }),
      showDirectPurchaseLink: false,
    };
  }

  const usePlanChangeCta = shouldUsePlanChangeCta(plan, context);

  if (usePlanChangeCta) {
    const canChangePlan = context.canChangePlan !== false;

    return {
      rateText,
      buttonHref: canChangePlan ? buildPlanChangeUrl(plan, context) : undefined,
      buttonLabel: plan.is_upgrade
        ? labels.upgradeLabel
        : labels.downgradeLabel,
      buttonAction: plan.is_upgrade ? 'upgrade' : 'downgrade',
      buttonDisabled: !canChangePlan,
      directPurchaseHref: buildPlanCtaUrl(plan, context, {
        directPurchase: true,
      }),
      showDirectPurchaseLink: false,
    };
  }

  const useGetStarted = shouldUseGetStartedLabel(plan, context);

  const buttonHref = usesDirectPurchaseOnlyVariant(plan, context)
    ? buildPlanCtaUrl(plan, context, { directPurchase: true })
    : buildPlanCtaUrl(plan, context);

  return {
    rateText,
    buttonHref,
    buttonLabel: useGetStarted ? labels.getStartedLabel : labels.freeTrialLabel,
    buttonAction: useGetStarted ? 'get-started' : 'start-free-trial',
    buttonDisabled: false,
    directPurchaseHref: buildPlanCtaUrl(plan, context, {
      directPurchase: true,
    }),
    showDirectPurchaseLink: shouldShowPlanDirectPurchaseLink(plan, context),
  };
}
